<template>
    <div v-if="Object.keys(dataQuote).length">
        <div class="rounded-lg flex space-x-10">
            <div class="w-4/12 flex flex-col shadow-md space-x-1">
                <div class="flex bg-blue-600 p-2 rounded-t-lg items-center">
                    <span class="text-white font-bold w-1/2">
                        {{ $t('labels.priceRequest') }}
                    </span>

                    <div class="w-1/2">
                        <hts-form-input
                            v-model="dataQuote.informationEditorPricingSelling.tempPricingRequest"
                            :min="1"
                            required
                            type="number"
                            readonly
                            prop="tempPricingRequest"
                            name="tempPricingRequest" />
                    </div>
                </div>
                <div class="flex p-2 items-center">
                    <span class="w-1/2">
                        {{ $t('labels.commission') }}<span class="text-error-500 ml-1">
                            *
                        </span>
                    </span>
                    <hts-form-input
                        v-model="dataQuote.informationEditorPricingSelling.commission"
                        class="w-1/2"
                        :min="1"
                        required
                        readonly
                        type="number"
                        prop="commission"
                        name="commission" />
                </div>
                <div class="flex rounded-b-lg p-2 items-center">
                    <span class="w-1/2">
                        {{ $t('labels.timeDebt') }}<span class="text-error-500 ml-1">
                            *
                        </span>
                    </span>
                    <hts-form-input
                        v-model="dataQuote.informationEditorPricingSelling.duration"
                        class="w-1/2"
                        :min="1"
                        required
                        readonly
                        type="number"
                        prop="duration"
                        name="duration">
                        <template #suffix>
                            {{ $t('labels.date') }}
                        </template>
                    </hts-form-input>
                </div>
            </div>
            <div
                v-if="checkPermission(layoutPermissions.partner_confirm_status_markup) && dataQuote.statusApprovalMarkup === 0"
                class="flex flex-col mt-2 space-y-2">
                <span class="text-blue-600 font-bold">
                    Phê duyệt markup
                </span>
                <div class="flex space-x-5">
                    <div
                        class="flex cursor-pointer items-center space-x-1 border p-2 rounded-full shadow-md hover:border-blue-600"
                        @click="handleShowPopupConfirmStatusMarkup">
                        <span class="">
                            {{ $t('labels.approve') }}
                        </span>
                        <hi-check-circle-icon
                            class="w-5 h-5 text-blue-600"
                            aria-hidden="true" />
                    </div>
                    <div
                        class="flex cursor-pointer items-center space-x-1 border p-2 rounded-full shadow-md hover:border-error-600"
                        @click="handleShowPopupRejectStatusMarkup">
                        <span>
                            {{ $t('labels.reject') }}
                        </span>
                        <hi-x-circle-icon
                            class="w-5 h-5 text-error-600"
                            aria-hidden="true" />
                    </div>
                </div>
            </div>
            <div
                v-else-if="!checkPermission(layoutPermissions.partner_confirm_status_markup) && dataQuote.statusApprovalMarkup === 0"
                class="flex space-x-2">
                <span class="text-blue-600 font-bold">
                    Trạng thái markup:
                </span>
                <div>
                    <div class="flex space-x-1 items-center">
                        <span>
                            {{ $t('labels.pendingApproval') }}
                        </span>
                        <hi-arrow-path-icon
                            class="w-5 h-5"
                            aria-hidden="true" />
                    </div>
                </div>
            </div>
            <div
                v-else-if="[1, 2].includes(dataQuote.statusApprovalMarkup)"
                class="flex space-x-2">
                <span class="text-blue-600 font-bold">
                    Trạng thái markup:
                </span>
                <div>
                    <div
                        v-if="dataQuote.statusApprovalMarkup === 1"
                        class="flex space-x-1 items-center text-success-600">
                        <span>
                            {{ $t('labels.approved') }}
                        </span>
                        <hi-check-circle-icon
                            class="w-5 h-5"
                            aria-hidden="true" />
                    </div>
                    <div
                        v-if="dataQuote.statusApprovalMarkup === 2"
                        class="flex space-x-1 items-center text-error-600">
                        <span>
                            {{ $t('labels.reject') }}
                        </span>
                        <hi-x-circle-icon
                            class="w-5 h-5"
                            aria-hidden="true" />
                    </div>
                </div>
            </div>
        </div>
        <hts-data-table
            ref="dataTables"
            class="table-step-1"
            :headers="headers || headerCharges"
            :single-click-edit="true"
            :items="dataQuote?.charges || []"
            :suppress-row-click-selection="true"
            :is-cell-clicked="false" />
        <view-price-detail ref="viewPriceDetail" />
        <view-price-vendor ref="viewPriceVendor" />
        <hts-modal
            :show="showRejectStatusMarkup"
            :title="$t('labels.confirmStatusMarkup')"
            type="confirm"
            :content="$t('labels.contentRejectStatusMarkup')"
            @hide="showRejectStatusMarkup = false"
            @confirm="rejectStatusMarkup" />
        <hts-modal
            :show="showConfirmStatusMarkup"
            :title="$t('labels.confirmStatusMarkup')"
            type="confirm"
            :content="$t('labels.contentConfirmStatusMarkup')"
            @close="showConfirmStatusMarkup = false"
            @confirm="confirmStatusMarkup" />
    </div>
</template>

<script lang="ts" setup>
import { addQuote } from '../../-components/ignore/imports/form'
import actions from '../../../partner/-components/actions.vue'
import MarkupView from '../../../quote/-components/markup-view.vue'
import ViewPriceDetail from '../../../quote/-components/view-price-detail.vue'
import ViewPriceVendor from '../../../quote/-components/view-price-vendor.vue'
import { ISearchPriceDetail } from '../../../quote/quote-request/-components/ignore/imports/form'
import { numberFormatter } from '~/pages/trucker-[id]/price/vendor/ignore/data'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'

const props = defineProps({
  dataQuote: {
    type: Object as PropType<addQuote>,
    required: true
  },
  headers: {
    type: Array
  }
})

const { dataQuote, headers } = toRefs(props)

const emits = defineEmits(['getData', 'refreshTables'])

const viewPriceDetail = ref()
const viewPriceVendor = ref()
const handleViewQuotePrice = (item: ISearchPriceDetail) => {
  if (item.type === 'internal') {
    viewPriceDetail.value && viewPriceDetail.value.open(item, dataQuote.value.priceOfflineFee)
  } else if (item.type === 'vendor') {
    viewPriceVendor.value && viewPriceVendor.value.open(item.priceVendorId)
  }
}

const showRejectStatusMarkup = ref(false)
const rejectStatusMarkup = () => {
  const api = $url.quote.rejectStatusMarkup
  $api(api, { uriParams: { ID: dataQuote.value._id } })
    .then((data: any) => {
      const resData: any = data?.data?.value
      if (resData?.success) {
        process.client && $toast().success($t('labels.updateSuccess', { name: $t('labels.quote') }))
        showRejectStatusMarkup.value = false
        emits('getData')
        emits('refreshTables')
      }
    })
    .catch(() => {})
}
const handleShowPopupRejectStatusMarkup = () => {
  showRejectStatusMarkup.value = true
}

const showConfirmStatusMarkup = ref(false)
const handleShowPopupConfirmStatusMarkup = () => {
  showConfirmStatusMarkup.value = true
}
const confirmStatusMarkup = () => {
  const api = $url.quote.approveStatusMarkup
  $api(api, { uriParams: { ID: dataQuote.value._id } })
    .then((data: any) => {
      const resData: any = data?.data?.value
      if (resData?.success) {
        process.client && $toast().success($t('labels.updateSuccess', { name: $t('labels.quote') }))
        showConfirmStatusMarkup.value = false
        emits('getData')
        emits('refreshTables')
      }
    })
    .catch(() => {})
}

const dataTables = ref()

const refresh = () => {
  dataTables.value && dataTables.value.gridApi.redrawRows()
}

const dataConfig = () => {
  return dataQuote.value.dataConfig
}

const statusApprovalMarkup = () => {
  return dataQuote.value.statusApprovalMarkup
}
const headerCharges = [
  {
    field: 'fleetVehicle',
    headerName: $t('labels.fleetVehicle'),
    sortable: true,
    valueGetter: (params: any) => {
      return params.data.type === 'internal' ? $t('labels.internal') : params?.data?.vendorName
    }
  },
  {
    field: 'price',
    headerName: $t('labels.purchasePrice'),
    sortable: true,
    valueGetter: (params: any) => {
      return typeof params?.data?.pricingEstimate?.total === 'number' ? numberFormatter(params.data.pricingEstimate.total) : 0
    }
  },
  {
    field: 'markup',
    key: 'markup',
    headerName: $t('labels.markup'),
    editable: (params: any) => {
      return params.node.selected
    },
    autoHeight: true,
    width: 100,
    cellRendererParams: {
      dataConfig,
      statusApprovalMarkup
    },
    cellRenderer: MarkupView
  },
  {
    field: 'profit',
    headerName: $t('labels.profit'),
    sortable: true,
    valueGetter: (params: any) => {
      const profit = params.data.pricingEstimate?.profit
      return typeof profit === 'number' ? numberFormatter($lodash.ceil(profit, -3)) : 0
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,
    cellRendererParams: {
      notCheck: true,
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: true,
          validate: (data: { [key: string]: any }) => {
            if (data.type === 'internal') {
              return checkPermission(layoutPermissions.quote_view_detail_quote_internal)
            } else if (data.type === 'vendor') {
              return checkPermission(layoutPermissions.price_vendor_management)
            } else {
              return false
            }
          },
          click: handleViewQuotePrice
        }
      ]
    }
  }
]

defineExpose({
  refresh
})
</script>
